import { Option, None, Some } from "@swiggy-private/js-fp";
import { ScrollElementIntoViewParams } from "@checkout-core/interfaces/Dom";

export const { whichTransitionEndEvent } = new (class {
    private _transitionEndEvent: Option<string> = None;
    public whichTransitionEndEvent = (el?: HTMLElement): string | undefined => {
        if (this._transitionEndEvent.isSome()) {
            return this._transitionEndEvent.unwrap();
        }

        if (typeof document === "undefined") {
            return;
        }

        let t: string;
        const transitions: { [key: string]: string } = {
            MozTransition: "transitionend",
            OTransition: "oTransitionEnd",
            transition: "transitionend",
            WebkitTransition: "webkitTransitionEnd",
        };

        el = el === undefined ? document.createElement("fakeelement") : el;

        for (t in transitions) {
            // eslint-disable-next-line no-prototype-builtins
            if (transitions.hasOwnProperty(t) && el.style[t as keyof CSSStyleDeclaration] !== undefined) {
                this._transitionEndEvent = Some(transitions[t]);
                return this._transitionEndEvent.unwrapOr("transitionend");
            }
        }
    };
})();

export const { whichAnimationEndEvent } = new (class {
    private _animationEndEvent: Option<string> = None;
    public whichAnimationEndEvent = (el?: HTMLElement): string | undefined => {
        if (this._animationEndEvent.isSome()) {
            return this._animationEndEvent.unwrap();
        }

        if (typeof document === "undefined") {
            return;
        }

        let t: string;
        const animations: { [key: string]: string } = {
            animation: "animationend",
            MozAnimation: "animationend",
            OTransition: "oAnimationEnd",
            WebkitAnimation: "webkitAnimationEnd",
        };

        el = el === undefined ? document.createElement("fakeelement") : el;

        for (t in animations) {
            // eslint-disable-next-line no-prototype-builtins
            if (animations.hasOwnProperty(t) && el.style[t as keyof CSSStyleDeclaration] !== undefined) {
                this._animationEndEvent = Some(animations[t]);
                return this._animationEndEvent.unwrapOr("animationend");
            }
        }
    };
})();

export const inputCaretMovementFixer = (): (() => void) => {
    const fn = (): void => {
        const element: HTMLInputElement | null = document.querySelector("input:focus");
        if (element && !element.getAttribute("ignore-cursor-fix")) {
            const val = element.value || "";
            element.value = val + "a";
            element.value = val;
        }
    };

    document.addEventListener("scroll", fn, true);

    return (): void => {
        document.removeEventListener("scroll", fn);
    };
};

export const getContentNode = (node: Element | null, keyboardHeight: number): HTMLElement | null => {
    if (node == null) {
        return null;
    }

    if (node.clientHeight > window.innerHeight - keyboardHeight && getComputedStyle(node)["overflowY"] === "scroll") {
        return node as HTMLElement;
    }

    return getContentNode(node.parentElement, keyboardHeight);
};

export const adjustContentIntoView = (contentNode: HTMLElement | null, keyboardHeight: number): void => {
    // screen height
    const scrollHeight = contentNode?.scrollHeight;
    const contentHeight = contentNode?.clientHeight;

    if (!scrollHeight || !contentHeight) {
        return;
    }

    const viewHeightAvailable = contentHeight - keyboardHeight;
    contentNode?.style.setProperty("max-height", `${viewHeightAvailable}px`);
};

export const restoreContentIntoView = (contentNode: HTMLElement | null): void => {
    contentNode?.style.removeProperty("max-height");
};

/**
 *
 * @param container - The scrollable container
 * @param verticalScrollValue - vertical scroll value of container
 */
export const scrollContainerVertically = (container: HTMLElement, verticalScrollValue?: number): void => {
    const y = verticalScrollValue || 0;

    if (container.scroll !== undefined) {
        container.scroll(0, Math.max(y, 0));
    } else {
        container.scrollTop = Math.max(y, 0);
    }
};

/**
 *
 * @param ScrollElementIntoViewParams
 */
export const scrollElementIntoViewOnKeyboardVisible = ({
    container,
    rect,
    bottomOffset = 16,
}: ScrollElementIntoViewParams): void => {
    let y: number | undefined;

    if (!container) {
        return;
    }

    const containerRect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    const rectBottom = rect.bottom;
    const containerRectBottom = containerRect.bottom - bottomOffset;

    if (rectBottom < containerRectBottom) {
        y = scrollTop - (containerRectBottom - rectBottom);
    } else {
        y = scrollTop + (rectBottom - containerRectBottom);
    }

    if (y === undefined) {
        return;
    }

    container.style.setProperty("-webkit-overflow-scrolling", "auto");
    scrollContainerVertically(container, y);
    container.style.setProperty("-webkit-overflow-scrolling", "touch");
};

export const updateHtmlElementHeightAccToKeyboardHeight = (
    htmlElemtnt: HTMLElement,
    isKeyBoardVisible: boolean,
    keyboardHeight: number,
): void => {
    const currentHeight = Number(htmlElemtnt.clientHeight ?? 0);
    if (isKeyBoardVisible) {
        htmlElemtnt.style.setProperty("min-height", `${currentHeight + keyboardHeight}px`);
    } else {
        htmlElemtnt.style.removeProperty("min-height");
    }
};
